<script setup>
import { useAlert } from 'dashboard/composables';
import AddCanned from './AddCanned.vue';
import EditCanned from './EditCanned.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

defineOptions({
  name: 'CannedResponseSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const cannedResponseAPI = ref({ message: '' });

const sortOrder = ref('asc');
const records = computed(() =>
  getters.getSortedCannedResponses.value(sortOrder.value)
);
const uiFlags = computed(() => getters.getUIFlags.value);

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

const toggleSort = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
};

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse');
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCannedResponses();
});

const showAlertMessage = message => {
  loading[activeResponse.value.id] = false;
  activeResponse.value = {};
  cannedResponseAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = (id) => {
  if (!id) {
    showAlertMessage('Error: Invalid canned response ID');
    return;
  }
  
  loading[id] = true;
  closeDeletePopup();
  deleteCannedResponse(id);
};

const tableHeaders = computed(() => {
  return [
    t('CANNED_MGMT.LIST.TABLE_HEADER.SHORT_CODE'),
    t('CANNED_MGMT.LIST.TABLE_HEADER.CONTENT'),
    t('CANNED_MGMT.LIST.TABLE_HEADER.ACTIONS'),
  ];
});

// File handling methods
const getFileIcon = fileType => {
  if (fileType === 'image') return 'image';
  if (fileType === 'video') return 'video';
  if (fileType === 'audio') return 'audio';
  return 'document';
};

const formatFileSize = bytes => {
  if (!bytes) return '';
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / k ** i).toFixed(2)) + ' ' + sizes[i];
};

const isImageAttachment = attachment => {
  return attachment.file_type === 'image' && attachment.thumb_url;
};
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('CANNED_MGMT.HEADER')"
      :description="$t('CANNED_MGMT.DESCRIPTION')"
      :link-text="$t('CANNED_MGMT.LEARN_MORE')"
      feature-name="canned_responses"
    >
      <template #actions>
        <woot-button
          class="button nice rounded-md"
          icon="add-circle"
          @click="openAddPopup"
        >
          {{ $t('CANNED_MGMT.HEADER_BTN_TXT') }}
        </woot-button>
      </template>
    </BaseSettingsHeader>

    <div class="mt-6 flex-1">
      <woot-loading-state
        v-if="uiFlags.fetchingList"
        :message="$t('CANNED_MGMT.LOADING')"
      />
      <p
        v-else-if="!records.length"
        class="flex flex-col items-center justify-center h-full text-base text-slate-600 dark:text-slate-300 py-8"
      >
        {{ $t('CANNED_MGMT.LIST.404') }}
      </p>
      <table
        v-else
        class="min-w-full overflow-x-auto divide-y divide-slate-75 dark:divide-slate-700"
      >
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 pr-4 text-left font-semibold text-slate-700 dark:text-slate-300 last:text-right"
          >
            <span v-if="thHeader !== tableHeaders[0]">
              {{ thHeader }}
            </span>
            <button
              v-else
              class="flex items-center p-0 cursor-pointer"
              @click="toggleSort"
            >
              <span class="mb-0">
                {{ thHeader }}
              </span>
              <fluent-icon
                class="ml-2 size-4"
                :icon="sortOrder === 'desc' ? 'chevron-up' : 'chevron-down'"
              />
            </button>
          </th>
        </thead>
        <tbody
          class="divide-y divide-slate-50 dark:divide-slate-800 text-slate-700 dark:text-slate-300"
        >
          <tr
            v-for="(cannedItem, index) in records"
            :key="cannedItem.short_code"
          >
            <td
              class="py-4 pr-4 truncate max-w-xs font-medium"
              :title="cannedItem.short_code"
            >
              {{ cannedItem.short_code }}
            </td>
            <td class="py-4 pr-4 md:break-all whitespace-normal">
              <div class="flex flex-col gap-2">
                <div v-if="cannedItem.content" class="content-text">
                  {{ cannedItem.content }}
                </div>

                <!-- Attachments Preview -->
                <div
                  v-if="
                    cannedItem.attachments &&
                    cannedItem.attachments.length > 0
                  "
                  class="attachments-section"
                >
                  <div class="attachments-header">
                    <fluent-icon icon="attach" class="attachment-icon" />
                    <span class="attachments-count">
                      {{ cannedItem.attachments.length }}
                      {{ $t('CANNED_MGMT.LIST.ATTACHMENTS_COUNT') }}
                    </span>
                  </div>
                  <div class="attachments-list">
                    <div
                      v-for="(attachment, index) in cannedItem.attachments.slice(0, 4)"
                      :key="index"
                      class="attachment-item"
                      :class="{
                        'image-preview': isImageAttachment(attachment),
                      }"
                    >
                      <!-- Image Thumbnail -->
                      <div
                        v-if="isImageAttachment(attachment)"
                        class="image-thumbnail"
                      >
                        <img
                          :src="attachment.thumb_url"
                          :alt="attachment.filename"
                          class="thumbnail-image"
                          @error="$event.target.style.display = 'none'"
                        />
                        <div class="image-overlay">
                          <fluent-icon icon="image" class="overlay-icon" />
                        </div>
                      </div>

                      <!-- File Icon for non-images -->
                      <div v-else class="file-icon">
                        <fluent-icon
                          :icon="getFileIcon(attachment.file_type)"
                          class="attachment-preview-icon"
                        />
                      </div>

                      <!-- File Info -->
                      <div class="file-info">
                        <span class="file-name">
                          {{ attachment.filename || `File ${index + 1}` }}
                        </span>
                        <span v-if="attachment.file_size" class="file-size">
                          {{ formatFileSize(attachment.file_size) }}
                        </span>
                      </div>
                    </div>
                    <div
                      v-if="cannedItem.attachments.length > 4"
                      class="more-files"
                    >
                      +{{ cannedItem.attachments.length - 4 }}
                      {{ $t('CANNED_MGMT.LIST.MORE_FILES') }}
                    </div>
                  </div>
                </div>
              </div>
            </td>
            <td class="py-4 flex justify-end gap-1">
              <woot-button
                v-tooltip.top="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
                variant="smooth"
                size="tiny"
                color-scheme="secondary"
                icon="edit"
                @click="openEditPopup(cannedItem)"
              />
              <woot-button
                v-tooltip.top="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
                variant="smooth"
                color-scheme="alert"
                size="tiny"
                icon="dismiss-circle"
                class-names="grey-btn"
                :is-loading="loading[cannedItem.id]"
                @click="openDeletePopup(cannedItem)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddCanned :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditCanned
        v-if="showEditPopup"
        :id="activeResponse.id"
        :edshort-code="activeResponse.short_code"
        :edcontent="activeResponse.content"
        :existing-attachments="activeResponse.attachments || []"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="() => confirmDeletion(activeResponse.id)"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>

<style scoped lang="scss">
.content-text {
  @apply text-sm;
}

.attachments-section {
  @apply mt-2;
}

.attachments-header {
  @apply flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 mb-2;
}

.attachment-icon {
  @apply w-3 h-3;
}

.attachments-count {
  @apply text-xs;
}

.attachments-list {
  @apply flex flex-wrap gap-2;
}

.attachment-item {
  @apply flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-xs border border-slate-200 dark:border-slate-600;

  &.image-preview {
    @apply p-1;
  }
}

.image-thumbnail {
  @apply relative w-12 h-12 rounded overflow-hidden bg-slate-200 dark:bg-slate-600;
}

.thumbnail-image {
  @apply w-full h-full object-cover;
}

.image-overlay {
  @apply absolute inset-0 bg-slate-900 bg-opacity-20 flex items-center justify-center;
}

.overlay-icon {
  @apply w-4 h-4 text-white;
}

.file-icon {
  @apply flex items-center justify-center w-8 h-8 bg-slate-200 dark:bg-slate-600 rounded;
}

.attachment-preview-icon {
  @apply w-4 h-4 text-slate-500 dark:text-slate-400;
}

.file-info {
  @apply flex flex-col gap-1;
}

.file-name {
  @apply text-slate-700 dark:text-slate-300 truncate max-w-24 font-medium;
}

.file-size {
  @apply text-slate-500 dark:text-slate-400;
}

.more-files {
  @apply flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 px-2 py-2;
}
</style>
